import { useEffect, useState } from "react";
import { Star, Send, Camera, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import SectionTitle from "./SectionTitle";

type Review = { id: string; name: string; rating: number; comment: string; created_at: string; image_url?: string | null };

export default function CustomerReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [hoveredStar, setHoveredStar] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    supabase.from("reviews").select("*").eq("is_approved", true).order("created_at", { ascending: false }).limit(6)
      .then(({ data }) => setReviews(data || []));
  }, []);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "حجم الصورة كبير", description: "الحد الأقصى 5 ميجابايت", variant: "destructive" });
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim()) return;
    setLoading(true);
    try {
      // Upload image if exists
      let imageUrl: string | null = null;
      if (imageFile) {
        const ext = imageFile.name.split(".").pop() || "jpg";
        const path = `reviews/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("gallery").upload(path, imageFile, { cacheControl: '31536000', upsert: false });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from("gallery").getPublicUrl(path);
          imageUrl = urlData.publicUrl;
        }
      }

      // Insert review - note: reviews table doesn't have image_url column per types,
      // so we append image to comment
      const commentWithImage = imageUrl
        ? `${comment.trim()}\n\nصورة مرفقة:\n${imageUrl}`
        : comment.trim();

      const { error } = await supabase.from("reviews").insert({
        name: name.trim(),
        rating,
        comment: commentWithImage,
      });
      if (error) throw error;

      toast({ title: "شكراً لتقييمك! ⭐", description: "سيظهر تقييمك بعد موافقة الإدارة" });
      setShowForm(false);
      setName(""); setComment(""); setRating(5); setPreviewImage(null); setImageFile(null);
    } catch (err: any) {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const avgRating = reviews.length > 0 ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "0";

  // Extract image from comment
  const extractReviewImage = (comment: string): { text: string; imageUrl: string | null } => {
    const match = comment.match(/\n*صورة مرفقة:\n(https?:\/\/[^\s]+)/);
    if (match) {
      return { text: comment.replace(/\n*صورة مرفقة:\n.*/s, "").trim(), imageUrl: match[1] };
    }
    return { text: comment, imageUrl: null };
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container">
        <SectionTitle title="آراء عملائنا" subtitle="ما يقوله عملاؤنا عن تجربتهم معنا" />

        {reviews.length > 0 && (
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="text-center">
              <p className="text-4xl font-black text-foreground">{avgRating}</p>
              <div className="flex gap-0.5 mt-1">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`h-4 w-4 ${s <= Math.round(Number(avgRating)) ? "text-primary fill-primary" : "text-muted"}`} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{reviews.length} تقييم</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {reviews.map((r) => {
            const { text, imageUrl } = extractReviewImage(r.comment);
            return (
              <div key={r.id} className="p-6 rounded-xl bg-card border border-border hover:shadow-card-hover transition-shadow">
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className={`h-4 w-4 ${j < r.rating ? "text-primary fill-primary" : "text-muted"}`} />
                  ))}
                </div>
                <p className="text-foreground text-sm mb-4 leading-relaxed">"{text}"</p>
                {(imageUrl || r.image_url) && (
                  <img src={imageUrl || r.image_url!} alt="صورة من العميل" className="w-full h-32 object-cover rounded-lg mb-3" loading="lazy" />
                )}
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm font-medium">— {r.name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("ar-YE")}</p>
                </div>
              </div>
            );
          })}
          {reviews.length === 0 && (
            <div className="col-span-3 text-center py-8 text-muted-foreground">لا توجد تقييمات بعد. كن أول من يقيّم!</div>
          )}
        </div>

        {!showForm ? (
          <div className="text-center">
            <Button onClick={() => setShowForm(true)} className="bg-gradient-woody text-primary-foreground hover:opacity-90">
              <Star className="h-4 w-4 ml-1" />
              أضف تقييمك
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-lg mx-auto bg-card border border-border rounded-xl p-6 space-y-4 animate-fade-in-up">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">الاسم</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="اسمك" required />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">التقييم</label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setRating(s)} onMouseEnter={() => setHoveredStar(s)} onMouseLeave={() => setHoveredStar(0)}>
                    <Star className={`h-7 w-7 cursor-pointer transition-all ${s <= (hoveredStar || rating) ? "text-primary fill-primary scale-110" : "text-muted"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">تعليقك</label>
              <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="اكتب تجربتك معنا..." rows={3} required />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">أرفق صورة (اختياري)</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-border cursor-pointer hover:border-primary/50 transition-colors">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">اختر صورة</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                </label>
                {previewImage && (
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border">
                    <img src={previewImage} alt="معاينة" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => { setPreviewImage(null); setImageFile(null); }} className="absolute top-0 right-0 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs flex items-center justify-center">×</button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="bg-gradient-woody text-primary-foreground" disabled={loading}>
                <Send className="h-4 w-4 ml-1" />{loading ? "جاري..." : "إرسال التقييم"}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setPreviewImage(null); setImageFile(null); }}>إلغاء</Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}